import * as THREE from 'three';
import { applyInteriorDark } from '../engine/interior';
import { ITEM_ICONS } from '../ui/iconManifest';
import { type ItemId, ITEMS } from './inventory';
import { terrainHeight } from '../engine/terrain';
import { nearestOnRay } from './aim';
import { SCATTER } from '../config';
import { itemScale } from '../engine/resizers';

// Items dropped into the world. Each is a small coloured crate that is TOSSED from its source (a
// parabolic arc -- flung off a hotbar, scattered from a smashed barrel), lands on the terrain, and
// bobs/spins so it reads as loot. Drops NEVER clump: each one claims a small footprint and spawn
// points push apart until free (and landed drops shove each other apart if they end up overlapping).
// Every drop expires after DROP_TTL_SEC (blinking out its last seconds). Walk near and press E to
// pick it up -- anyone can, so dropped loot is shareable. Client-only for now.

const DROP_TTL_SEC = 600;    // 10 minutes on the ground, then gone
const BLINK_SEC = 8;         // warning blink at the end of life
const MIN_SEP = 0.62;        // two drops never rest closer than this (their "collider" footprint)
const GRAV = 12;             // toss-arc gravity (a bit floaty reads better than 9.8 at this scale)

// ONE crate geometry + one material per item colour, shared by every drop (Phase 2: no per-drop
// geometry/material churn). Neither is ever disposed -- the cache is small and lives for the session.
const CRATE_GEO = new THREE.BoxGeometry(0.4, 0.4, 0.4);
const CRATE_MATS = new Map<number, THREE.MeshLambertMaterial>();
function crateMat(color: number): THREE.MeshLambertMaterial {
  let m = CRATE_MATS.get(color);
  if (!m) { m = new THREE.MeshLambertMaterial({ color, flatShading: true }); applyInteriorDark(m); CRATE_MATS.set(color, m); }
  return m;
}

// Items with owner-drawn icons drop as a FLOATING 2D SPRITE of that icon (always camera-facing, Minecraft
// style -- owner pick over 3D minis). One texture + one sprite material per item id, cached for the session;
// items without an icon keep the classic colour crate. alphaTest keeps the cutout crisp without sort issues.
const DROP_SPRITE_SCALE = 0.55; // fallback for any item with no saved override (F4 -> OBJECTS -> Icons, engine/resizers.ts)
const ICON_LOADER = new THREE.TextureLoader();
const SPRITE_MATS = new Map<string, THREE.SpriteMaterial>();
// Exported so the F4 calibration tool (ui/objectLab.ts) previews the EXACT same material/texture a real
// ground drop uses -- no separate loading path to fall out of sync.
export function spriteMat(id: string): THREE.SpriteMaterial {
  let m = SPRITE_MATS.get(id);
  if (!m) {
    const tex = ICON_LOADER.load(`/icons/${id}.png`);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter; // chunky pixel art stays crisp up close
    m = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.3 });
    SPRITE_MATS.set(id, m);
  }
  return m;
}

// HOVER: a crisp white OUTLINE baked around the icon (silhouette stamped at 8 offsets under the art),
// swapped in as the sprite's material while aimed at -- reads as selection, no ghost-copy blur. Built
// lazily per item the first time it's hovered (needs the icon image decoded); until then the hover
// falls back to no-op and the next aim builds it. Cached for the session like the base materials.
const OUTLINE_PAD = 6;
const OUTLINED_MATS = new Map<string, THREE.SpriteMaterial>();
function outlinedMat(id: string): THREE.SpriteMaterial | null {
  const hit = OUTLINED_MATS.get(id);
  if (hit) return hit;
  const img = spriteMat(id).map!.image as HTMLImageElement | undefined;
  if (!img || !img.complete || !img.naturalWidth) return null; // icon not decoded yet
  const w = img.naturalWidth + OUTLINE_PAD * 2, h = img.naturalHeight + OUTLINE_PAD * 2;
  const sil = document.createElement('canvas'); sil.width = w; sil.height = h;
  const sc = sil.getContext('2d')!;
  sc.drawImage(img, OUTLINE_PAD, OUTLINE_PAD);
  sc.globalCompositeOperation = 'source-in';
  sc.fillStyle = '#fff'; sc.fillRect(0, 0, w, h); // white silhouette of the icon
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  for (const [ox, oy] of [[-2, 0], [2, 0], [0, -2], [0, 2], [-2, -2], [2, -2], [-2, 2], [2, 2]]) ctx.drawImage(sil, ox, oy);
  ctx.drawImage(img, OUTLINE_PAD, OUTLINE_PAD); // the art itself on top
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  const m = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.3 });
  OUTLINED_MATS.set(id, m);
  return m;
}
// Sprite scale that keeps the ART the same on-screen size when the padded outline texture is active
// (the padding would otherwise shrink the icon = the "zoom state" flicker).
function outlinedScale(id: string): number {
  const img = spriteMat(id).map!.image as HTMLImageElement | undefined;
  return itemScale(id, DROP_SPRITE_SCALE) * (img && img.naturalWidth ? (img.naturalWidth + OUTLINE_PAD * 2) / img.naturalWidth : 1);
}

// GROUND SHADOW: a soft round blob under every drop (shared radial-gradient texture, one material).
// The drop art floats and bobs; the blob anchors it to the ground like every other object's shadow.
const SHADOW_MAT = (() => {
  const c = document.createElement('canvas'); c.width = 64; c.height = 64;
  const ctx = c.getContext('2d')!;
  const grad = ctx.createRadialGradient(32, 32, 4, 32, 32, 30);
  grad.addColorStop(0, 'rgba(0,0,0,0.42)');
  grad.addColorStop(0.7, 'rgba(0,0,0,0.22)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad; ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  return new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
})();
const SHADOW_GEO = new THREE.PlaneGeometry(1, 1);
function makeShadow(): THREE.Mesh {
  const m = new THREE.Mesh(SHADOW_GEO, SHADOW_MAT);
  m.rotation.x = -Math.PI / 2;
  m.scale.setScalar(0.5);
  m.renderOrder = 1; // over the terrain, under everything else transparent
  return m;
}

// The visual for one drop: icon sprite when the item has one, else the colour crate. The sprite is
// lifted a touch so it hovers above the ground line while the group bobs.
function dropVisual(id: ItemId): THREE.Object3D {
  if (ITEM_ICONS.has(id)) {
    const s = new THREE.Sprite(spriteMat(id));
    s.scale.setScalar(itemScale(id, DROP_SPRITE_SCALE));
    s.position.y = 0.1;
    return s;
  }
  return new THREE.Mesh(CRATE_GEO, crateMat(ITEMS[id].color));
}

// Local world save shape for one ground drop.
export interface SavedDrop { id: string; n: number; x: number; z: number; age: number; }

interface Dropped {
  group: THREE.Group;
  shadow: THREE.Mesh;   // round ground blob (lives in the field group, tracks the drop)
  dropId: number; // stable id so a pickup can be addressed over the wire (spec section 19)
  id: ItemId;
  amount: number;
  pos: THREE.Vector3;   // settled anchor (bob oscillates around pos.y)
  vel: THREE.Vector3;   // toss velocity while airborne
  airborne: boolean;
  age: number;          // seconds on the ground (TTL)
  spin: number;
}

export class DroppedItemField {
  readonly group = new THREE.Group();
  private readonly list: Dropped[] = [];
  private nextId = 1;
  private lastTime = -1;
  // MP: fired for every NEW local drop (toss, salvage loot, harvest overflow, corpse chop) with its
  // FINAL resting position, so NetBackend can broadcast it and the same loot appears on every screen.
  // Remote-applied drops (spawnRemote) do NOT fire this, so there is no rebroadcast loop.
  onSpawn: ((id: ItemId, amount: number, x: number, z: number, yaw: number) => void) | null = null;
  // One shared HOVER outline: a slightly up-scaled BackSide box drawn behind the hovered crate, so its silhouette
  // reads as a thin ring. Diff-tracked (like the node glow) so it follows the hovered drop or hides. One mesh, no
  // per-drop material mutation (so nothing leaks when a drop is picked up).
  private readonly outline = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.4, 0.4),
    // Neutral SK tan (the hotbar's selected-slot tone) instead of warm white -- the drop hover now
    // speaks the same design language as the rest of the UI (owner 2026-07-12).
    new THREE.MeshBasicMaterial({ color: 0xcfc5b4, side: THREE.BackSide }),
  );
  private hovered: Dropped | null = null;

  constructor() {
    this.outline.scale.setScalar(1.2);
    this.outline.visible = false;
    this.outline.renderOrder = 4;
    this.group.add(this.outline);
  }

  // The icon sprite of a drop, or null for a crate drop.
  private spriteOf(d: Dropped): THREE.Sprite | null {
    const c = d.group.children[0];
    return c && (c as THREE.Sprite).isSprite ? (c as THREE.Sprite) : null;
  }

  // Highlight the drop the player is aiming at (cursor iso / crosshair FP), or clear it. Driven each frame.
  // Icon drops swap to their baked white-OUTLINE material (crisp selection, same on-screen art size);
  // crate drops keep the classic BackSide shell.
  setHovered(d: Dropped | null): void {
    if (d !== this.hovered) {
      if (this.hovered) { // restore the previous drop's base look
        const s = this.spriteOf(this.hovered);
        if (s) { s.material = spriteMat(this.hovered.id); s.scale.setScalar(itemScale(this.hovered.id, DROP_SPRITE_SCALE)); }
      }
      this.hovered = d;
      let boxed = !!d;
      if (d) {
        const s = this.spriteOf(d);
        if (s) {
          boxed = false;
          const om = outlinedMat(d.id);
          if (om) { s.material = om; s.scale.setScalar(outlinedScale(d.id)); }
        }
      }
      this.outline.visible = boxed;
    }
    if (d && this.outline.visible) this.outline.position.copy(d.group.position); // follow the live bob/spin
  }

  // The nearest landed drop is at least MIN_SEP from (x,z)? Airborne drops don't block (they'll shove on landing).
  private spotFree(x: number, z: number, ignore?: Dropped): boolean {
    for (const d of this.list) {
      if (d === ignore || d.airborne) continue;
      const dx = d.pos.x - x, dz = d.pos.z - z;
      if (dx * dx + dz * dz < MIN_SEP * MIN_SEP) return false;
    }
    return true;
  }

  // Find a rest spot near (x,z) that no other drop claims: try the point, then spiral outward.
  private findFreeSpot(x: number, z: number, ignore?: Dropped): { x: number; z: number } {
    if (this.spotFree(x, z, ignore)) return { x, z };
    for (let ring = 1; ring <= 6; ring++) {
      const r = ring * MIN_SEP * 0.9;
      const a0 = Math.random() * Math.PI * 2;
      for (let k = 0; k < 8; k++) {
        const a = a0 + (k / 8) * Math.PI * 2;
        const px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
        if (this.spotFree(px, pz, ignore)) return { x: px, z: pz };
      }
    }
    return { x, z }; // crowded beyond reason: give up gracefully
  }

  // Spawn one crate of `amount` of `id`, TOSSED from `from` toward the free spot nearest (tx,tz).
  private toss(id: ItemId, amount: number, from: THREE.Vector3, tx: number, tz: number): void {
    const spot = this.findFreeSpot(tx, tz);
    const g = new THREE.Group();
    g.add(dropVisual(id)); // icon sprite when the item has one, else the colour crate
    g.position.copy(from);
    this.group.add(g);
    const shadow = makeShadow();
    shadow.position.set(from.x, terrainHeight(from.x, from.z) + 0.02, from.z);
    this.group.add(shadow);
    // Solve a flat-ish arc to the spot: pick a flight time from the distance, derive v so it lands there.
    const groundY = terrainHeight(spot.x, spot.z) + 0.25;
    const dx = spot.x - from.x, dz = spot.z - from.z;
    const dist = Math.hypot(dx, dz);
    const T = Math.max(0.28, Math.min(0.9, dist * 0.16 + 0.22));
    const vel = new THREE.Vector3(
      dx / T,
      (groundY - from.y + 0.5 * GRAV * T * T) / T, // classic ballistic solve for the landing height
      dz / T,
    );
    const rec: Dropped = {
      group: g, shadow, dropId: this.nextId++, id, amount,
      pos: new THREE.Vector3(spot.x, groundY, spot.z),
      vel, airborne: true, age: 0, spin: Math.random() * Math.PI * 2,
    };
    g.userData.drop = rec; // backref so a cursor/crosshair ray can resolve the exact hovered drop
    this.list.push(rec);
    this.onSpawn?.(id, amount, spot.x, spot.z, rec.spin); // MP: broadcast this drop at its final resting spot
  }

  // REMOTE drop: another client scattered/tossed this loot; spawn it SETTLED at the exact broadcast
  // position (no arc, no re-jitter) so both worlds agree on where it rests -> a pickup on either side
  // position-matches. Never fires onSpawn (would loop the broadcast back). remSec = the server's remaining
  // despawn seconds (persisted drops): the age pre-advances so this drop blinks out at the same real moment
  // on every screen instead of restarting the full TTL for a late joiner.
  spawnRemote(id: ItemId, amount: number, x: number, z: number, yaw: number, remSec?: number): void {
    const g = new THREE.Group();
    g.add(dropVisual(id));
    const pos = new THREE.Vector3(x, terrainHeight(x, z) + 0.25, z);
    g.position.copy(pos);
    this.group.add(g);
    const shadow = makeShadow();
    shadow.position.set(pos.x, pos.y - 0.23, pos.z);
    this.group.add(shadow);
    const age = remSec != null ? Math.max(0, DROP_TTL_SEC - remSec) : 0;
    const rec: Dropped = { group: g, shadow, dropId: this.nextId++, id, amount, pos, vel: new THREE.Vector3(), airborne: false, age, spin: yaw };
    g.userData.drop = rec;
    this.list.push(rec);
  }

  // RECONNECT RECONCILE: make the local ground match the server's persisted drops list. Removes SETTLED drops
  // the server no longer has (loot someone picked while this client was disconnected -- leaving the ghost would
  // let this player "pick it up" again and mint a duplicate), and adds drops it missed. Fresh (<30s) and
  // airborne drops are left alone: this client's own blip-time tosses are still riding the unacked op ring and
  // must not be culled before their dropAt reaches the authority.
  reconcileRemote(server: Array<{ item: string; n: number; x: number; z: number; yaw: number; rem: number }>): void {
    const AGE_GUARD_SEC = 30;
    const near = (ax: number, az: number, bx: number, bz: number): boolean => { const dx = ax - bx, dz = az - bz; return dx * dx + dz * dz <= 0.25; };
    for (let i = this.list.length - 1; i >= 0; i--) {
      const d = this.list[i];
      if (d.airborne || d.age < AGE_GUARD_SEC) continue;
      if (!server.some((s) => s.item === d.id && near(s.x, s.z, d.pos.x, d.pos.z))) this.removeAt(i);
    }
    for (const s of server) {
      if (!(s.item in ITEMS)) continue;
      if (!this.list.some((d) => d.id === s.item && near(d.pos.x, d.pos.z, s.x, s.z))) {
        this.spawnRemote(s.item as ItemId, s.n, s.x, s.z, s.yaw, s.rem);
      }
    }
  }

  // The drop the player is AIMING at -- cursor in isometric, centre crosshair in first-person (the raycaster is
  // fed screen-centre in FP). Nearest hit first (three sorts intersections by distance). Range-gate at the call site.
  aimed(raycaster: THREE.Raycaster): Dropped | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.drop) return o.userData.drop as Dropped; o = o.parent; }
    }
    return null;
  }

  // FORGIVING aim: a precise mesh hit first, else the drop the ray passes within `tol` metres of (shared nearestOnRay).
  // Drops are small cubes, so this makes "point at it" reliable without falling back to auto-nearest.
  aimedSoft(raycaster: THREE.Raycaster, tol: number): Dropped | null {
    return this.aimed(raycaster) ?? nearestOnRay(raycaster, this.list.map((d) => ({ x: d.group.position.x, y: d.group.position.y, z: d.group.position.z, rec: d })), tol);
  }

  // FLING `amount` of `id` from the player's hands to the ground ahead of (px,pz) facing `yaw` -- a real
  // toss arc, landing ~2-3.5m out with a little sideways scatter so repeated drops fan out.
  drop(id: ItemId, amount: number, px: number, pz: number, yaw: number): void {
    const side = (Math.random() - 0.5) * 1.6;
    const reach = 2.2 + Math.random() * 1.3;
    const tx = px + Math.sin(yaw) * reach + Math.cos(yaw) * side;
    const tz = pz + Math.cos(yaw) * reach - Math.sin(yaw) * side;
    const from = new THREE.Vector3(px + Math.sin(yaw) * 0.4, terrainHeight(px, pz) + 1.35, pz + Math.cos(yaw) * 0.4);
    this.toss(id, amount, from, tx, tz);
  }

  // SCATTER loot on the ground around (cx,cz): each item type drops as ONE bundled pile (8 scrap = one pickup of
  // 8, not 8 cubes) flung to its own jittered spot, so different items land apart but a stack stays whole.
  scatter(items: Array<{ id: ItemId; amount: number }>, cx: number, cz: number): void {
    const from = new THREE.Vector3(cx, terrainHeight(cx, cz) + 0.9, cz);
    for (const it of items) {
      if (it.amount <= 0) continue;
      const ang = Math.random() * Math.PI * 2, r = SCATTER.rMin + Math.random() * (SCATTER.rMax - SCATTER.rMin);
      this.toss(it.id, it.amount, from, cx + Math.cos(ang) * r, cz + Math.sin(ang) * r);
    }
  }

  // --- LOCAL WORLD SAVE (game/persist.ts). Drops persist with their TTL age so a reload can't
  // refresh a pile forever; restore() re-creates each one SETTLED (it was already resting). ---
  serialize(): SavedDrop[] {
    return this.list.map((d) => ({ id: d.id, n: d.amount, x: d.pos.x, z: d.pos.z, age: d.age }));
  }
  restore(id: ItemId, amount: number, x: number, z: number, age: number): void {
    const spot = this.findFreeSpot(x, z);
    const g = new THREE.Group();
    g.add(dropVisual(id));
    const pos = new THREE.Vector3(spot.x, terrainHeight(spot.x, spot.z) + 0.25, spot.z);
    g.position.copy(pos);
    this.group.add(g);
    const shadow = makeShadow();
    shadow.position.set(pos.x, pos.y - 0.23, pos.z);
    this.group.add(shadow);
    const rec: Dropped = {
      group: g, shadow, dropId: this.nextId++, id, amount, pos,
      vel: new THREE.Vector3(), airborne: false, age: Math.max(0, age), spin: Math.random() * Math.PI * 2,
    };
    g.userData.drop = rec;
    this.list.push(rec);
  }

  // Live drop count (dev/MP-test hook: verify loot scattered by one client appears on another).
  count(): number { return this.list.length; }

  // Nearest dropped item within range of (px,pz), or null (for the E prompt + pickup).
  nearest(px: number, pz: number, range: number): Dropped | null {
    let best: Dropped | null = null, bestSq = range * range;
    for (const d of this.list) {
      const dx = d.pos.x - px, dz = d.pos.z - pz, sq = dx * dx + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = d; }
    }
    return best;
  }

  labelOf(d: Dropped): string { return `${ITEMS[d.id].name} (${d.amount})`; }
  itemOf(d: Dropped): { id: ItemId; amount: number } { return { id: d.id, amount: d.amount }; }
  idOf(d: Dropped): number { return d.dropId; }

  // What a drop holds WITHOUT removing it (the authority checks inventory room before granting).
  peekById(dropId: number): { id: ItemId; amount: number } | null {
    const d = this.list.find((x) => x.dropId === dropId);
    return d ? { id: d.id, amount: d.amount } : null;
  }

  // Remove a drop by its stable id and return what it held (so the authority can grant it). Null if gone
  // already (someone else grabbed it first).
  pickupById(dropId: number): { id: ItemId; amount: number } | null {
    const i = this.list.findIndex((d) => d.dropId === dropId);
    if (i < 0) return null;
    const d = this.list[i];
    this.removeAt(i);
    return { id: d.id, amount: d.amount };
  }

  // Where a drop sits (for the network relay), or null if it is gone.
  posById(dropId: number): { x: number; z: number } | null {
    const d = this.list.find((x) => x.dropId === dropId);
    return d ? { x: d.pos.x, z: d.pos.z } : null;
  }

  // Remove the nearest drop of `item` within range of (x,z) and return what it held, or null. Used when a
  // REMOTE player picked something up: their client addressed it by its own dropId, which does not match
  // ours (stable cross-client drop ids land in M2), so position + item is the convergence key.
  takeNearOf(x: number, z: number, item: ItemId, range: number): { id: ItemId; amount: number } | null {
    let best = -1, bestSq = range * range;
    for (let i = 0; i < this.list.length; i++) {
      const d = this.list[i];
      if (d.id !== item) continue;
      const dx = d.pos.x - x, dz = d.pos.z - z, sq = dx * dx + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = i; }
    }
    if (best < 0) return null;
    const d = this.list[best];
    this.removeAt(best);
    return { id: d.id, amount: d.amount };
  }

  private removeAt(i: number): void {
    const d = this.list[i];
    if (this.hovered === d) this.setHovered(null); // clear the outline if we just removed the hovered drop
    this.list.splice(i, 1);
    this.group.remove(d.group); // geometry + materials are shared/cached -- nothing to dispose per drop
    this.group.remove(d.shadow);
  }

  update(time: number): void {
    const dt = this.lastTime < 0 ? 0 : Math.min(0.1, Math.max(0, time - this.lastTime));
    this.lastTime = time;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const d = this.list[i];
      d.age += dt;
      if (d.age >= DROP_TTL_SEC) { this.removeAt(i); continue; } // expired: gone for good
      if (d.airborne) {
        // Ballistic flight to the claimed spot; land when we pass the ground.
        d.vel.y -= GRAV * dt;
        d.group.position.x += d.vel.x * dt;
        d.group.position.y += d.vel.y * dt;
        d.group.position.z += d.vel.z * dt;
        d.group.rotation.x += dt * 6; d.group.rotation.z += dt * 5; // tumble in the air
        d.shadow.position.set(d.group.position.x, terrainHeight(d.group.position.x, d.group.position.z) + 0.02, d.group.position.z); // blob chases the flier along the ground
        const gy = terrainHeight(d.group.position.x, d.group.position.z) + 0.25;
        if (d.vel.y < 0 && d.group.position.y <= gy) {
          d.airborne = false;
          // Land where physics put us -- but shove off any drop already resting there (the collider).
          const spot = this.findFreeSpot(d.group.position.x, d.group.position.z, d);
          d.pos.set(spot.x, terrainHeight(spot.x, spot.z) + 0.25, spot.z);
          d.group.rotation.x = 0; d.group.rotation.z = 0;
          d.group.position.copy(d.pos);
          d.shadow.position.set(d.pos.x, d.pos.y - 0.23, d.pos.z);
        }
        continue;
      }
      // Settled: bob + spin around the anchor, blink out the final seconds. The shadow breathes
      // opposite the bob (item up -> blob slightly smaller/fainter) so the float reads grounded.
      d.group.rotation.y = time * 1.2 + d.spin;
      const bob = Math.sin(time * 2 + d.pos.x) * 0.06;
      d.group.position.y = d.pos.y + bob;
      d.shadow.scale.setScalar(0.5 - bob * 0.6);
      const left = DROP_TTL_SEC - d.age;
      d.group.visible = left > BLINK_SEC || Math.floor(time * 5) % 2 === 0;
      d.shadow.visible = d.group.visible;
    }
  }
}

export type { Dropped };
